/**
 * The cohort form, in words (24.09.2026).
 *
 * The form used to be one long scroll of seventeen fields covering four
 * different subjects, so it now has tabs — and tabs need two things a scroll
 * did not: a line that says what this cohort is while you work on any tab,
 * and a way to point at the tab holding the field you forgot.
 */

const MONTHS = ["ian.", "feb.", "mar.", "apr.", "mai", "iun.",
  "iul.", "aug.", "sept.", "oct.", "nov.", "dec."];

/** `2026-09-14` → `14 sept.`, without dragging a date library into it. */
export function shortDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
}

/** Where this cohort stands at Logiscool, for the summary chip. */
export function limoState(group) {
  if (group?.limo_group_id) return "creată";
  if (group?.limo_test_group_id) return "doar de probă";
  return "necreată";
}

/**
 * The line above the tabs: period, how often, how full, and Logiscool.
 *
 * Built from the FORM, not from the saved group, so it follows what you are
 * typing — except the seats taken and the Logiscool state, which only the
 * server knows.
 */
export function groupSummary(form = {}, group = null) {
  const out = [];
  const sessions = Array.isArray(form.sessions) ? form.sessions : null;
  const dates = sessions ? sessions.map((s) => s.date).filter(Boolean).sort() : [];
  const start = sessions ? dates[0] : form.start_date;
  const end = sessions ? dates[dates.length - 1] : form.end_date;
  if (start) out.push(end && end !== start ? `${shortDate(start)} – ${shortDate(end)}` : shortDate(start));
  if (sessions) {
    out.push(`${sessions.length} ${sessions.length === 1 ? "sesiune" : "sesiuni"}`);
  } else if ((form.schedule_days || []).length) {
    out.push(form.schedule_days.join(", "));
  }
  const seats = Number(form.max_seats) || 0;
  if (seats) out.push(`${Number(group?.enrolled_count || 0)}/${seats} locuri`);
  out.push(`Logiscool: ${limoState(group)}`);
  return out;
}

/**
 * Which tab holds which missing field.
 *
 * Mirrors the checks in the form's own save(): the browser cannot enforce
 * `required` on an input that is on another tab — it refuses to submit and
 * cannot focus what it is complaining about — so the rules live here and the
 * form jumps to the tab instead.
 */
export function tabIssues(form = {}, { sessionsBlocking = false } = {}) {
  const withSessions = Array.isArray(form.sessions);
  const issues = { grupa: [], program: [], predare: [] };
  if (!String(form.name || "").trim()) issues.grupa.push("Numele grupei");
  if (withSessions && form.sessions.length === 0) issues.program.push("Cel puțin o sesiune");
  if (withSessions && sessionsBlocking) issues.program.push("Sesiuni cu erori");
  if (!withSessions && !form.start_date) issues.program.push("Data de start");
  return issues;
}

/** The first tab with something missing, in the order the tabs are shown. */
export function firstIssueTab(issues, order = ["grupa", "program", "predare"]) {
  return order.find((key) => (issues?.[key] || []).length > 0) || null;
}

/** The fields the form owns — what "unsaved" is measured over. */
export const FORM_FIELDS = [
  "name", "description", "start_date", "end_date", "schedule_days", "schedule_time",
  "format", "max_seats", "status", "show_seats", "trainer_id", "limo_account_id",
  "access_link", "session_reminders_enabled", "notes", "sessions", "portal_enabled",
  "access", "billing_mode", "institution_name", "institution_contact", "price", "prima_card", "regime",
  // The closing margin (19.10.2026): changing only it left the form "saved".
  "enrolment_close_minutes",
];

// A <select> hands back text, the server a number: `30` and `"30"` are the
// same margin, and `null` and `""` are both "the default".
const SAME_AS_TEXT = new Set(["enrolment_close_minutes"]);

function comparable(k, v) {
  if (SAME_AS_TEXT.has(k)) return String(v ?? "");
  return JSON.stringify(v ?? null);
}

/**
 * Does the form differ from what was last saved?
 *
 * The Logiscool panel acts on the cohort as the SERVER has it, so picking a
 * trainer and pressing "Verifică ce s-ar crea" used to show the old one until
 * you saved and reopened. Knowing this lets the panel save first instead.
 */
export function formDirty(form, saved) {
  if (!saved) return true;
  return FORM_FIELDS.some((k) => comparable(k, form?.[k]) !== comparable(k, saved?.[k]));
}
